import { buildXlsx, safePrecision, type XlsxRow, type XlsxSheet, type XlsxStyle } from '@matix/utils';
import { moneyPrecision } from '../../store/currency';

/**
 * "Export to Excel" for the cashier's reports.
 *
 * One sheet per report, laid out the way the screen lays it out — a title
 * block, then the same sections and the same columns in the same order — so a
 * spreadsheet and a printed copy of the same run are the same document.
 *
 * Money goes in as a NUMBER at the tenant currency's precision, never as
 * preformatted text: the point of exporting to Excel is that the accountant
 * can sum the column, and `"1,234.500"` is a string that sums to zero. The
 * same goes for quantities, counts and percentages; only labels are text.
 *
 * The workbook writer itself is `@matix/utils/xlsx` — no library, so the
 * offline bundle does not grow by a megabyte for one button.
 */

/** How a column's cells are formatted — text, or one of the number shapes. */
export type ColumnKind = 'text' | 'center' | 'int' | 'money' | 'qty' | 'pct';

export interface ExcelColumn {
  label: string;
  kind?: ColumnKind;
  /** Characters; omitted columns are auto-sized from their content. */
  width?: number;
}

/**
 * A cell value. `{ v, kind }` overrides the column's formatting for that one
 * cell — a count sitting in a money column ("Total Orders" under Revenues)
 * would otherwise print as currency.
 */
export type ExcelCell = string | number | null | { v: string | number | null; kind: ColumnKind };
/** A row of cells, or a full-width note that belongs under the row above it. */
export type ExcelRow = ExcelCell[] | { note: string };

function cellValue(c: ExcelCell | undefined): string | number | null {
  if (c != null && typeof c === 'object') return c.v;
  return c ?? null;
}
function cellKind(c: ExcelCell | undefined, fallback: ColumnKind): ColumnKind {
  return c != null && typeof c === 'object' ? c.kind : fallback;
}

const BODY_STYLE: Record<ColumnKind, XlsxStyle> = {
  text: 'text', center: 'textCenter', int: 'int', money: 'money', qty: 'qty', pct: 'pct',
};
const TOTAL_STYLE: Record<ColumnKind, XlsxStyle> = {
  text: 'totalText', center: 'totalText', int: 'totalInt',
  money: 'totalMoney', qty: 'totalQty', pct: 'totalPct',
};

/**
 * Accumulates a report's sheet. Blocks are appended in screen order; the title
 * and section banners are merged across the widest table only once everything
 * is known, which is why `build()` — not the individual calls — decides spans.
 */
export class ReportSheetBuilder {
  private readonly rows: XlsxRow[] = [];
  /** Rows to stretch across the full width once the width is known. */
  private readonly fullSpan: number[] = [];
  private columns = 1;
  private freeze = 0;

  /** The report's name and the scope/range line under it. */
  title(title: string, subtitle?: string): this {
    this.pushFull({ value: title, style: 'title' }, 24);
    if (subtitle) this.pushFull({ value: subtitle, style: 'subtitle' });
    this.blank();
    // Everything written so far stays put while the rows scroll.
    this.freeze = this.rows.length;
    return this;
  }

  /** A banner over the block that follows ("Payment Types", a category name). */
  section(label: string): this {
    this.pushFull({ value: label, style: 'section' }, 20);
    return this;
  }

  blank(): this {
    this.rows.push({ cells: [] });
    return this;
  }

  /**
   * A table. `header: false` drops the header band — used for the label/value
   * blocks (Revenues, Totals) that the screen draws without one.
   */
  table(
    columns: ExcelColumn[],
    rows: ExcelRow[],
    opts: { totals?: ExcelCell[]; header?: boolean; emptyText?: string } = {},
  ): this {
    const kinds = columns.map((c) => c.kind ?? 'text');
    this.columns = Math.max(this.columns, columns.length);

    if (opts.header !== false) {
      this.rows.push({ cells: columns.map((c) => ({ value: c.label, style: 'header' as XlsxStyle })) });
    }
    if (rows.length === 0 && opts.emptyText) {
      this.pushFull({ value: opts.emptyText, style: 'note' });
    }
    for (const row of rows) {
      if (!Array.isArray(row)) {
        this.pushFull({ value: row.note, style: 'note' });
        continue;
      }
      this.rows.push({
        cells: columns.map((_, i) => ({
          value: cellValue(row[i]),
          style: BODY_STYLE[cellKind(row[i], kinds[i] ?? 'text')],
        })),
      });
    }
    if (opts.totals) {
      const totals = opts.totals;
      this.rows.push({
        cells: columns.map((_, i) => ({
          value: cellValue(totals[i]),
          style: TOTAL_STYLE[cellKind(totals[i], kinds[i] ?? 'text')],
        })),
      });
    }
    this.blank();
    return this;
  }

  build(name: string, opts: { rightToLeft: boolean }): XlsxSheet {
    for (const index of this.fullSpan) {
      const cell = this.rows[index]?.cells[0];
      if (cell && typeof cell === 'object') cell.span = this.columns;
    }
    return {
      name,
      rows: this.rows,
      rightToLeft: opts.rightToLeft,
      freezeRows: this.freeze,
      // Anything past five columns wants the wider sheet when it is printed.
      landscape: this.columns > 5,
    };
  }

  private pushFull(cell: { value: string; style: XlsxStyle }, height?: number): void {
    this.fullSpan.push(this.rows.length);
    this.rows.push(height ? { cells: [cell], height } : { cells: [cell] });
  }
}

/**
 * Build and hand the workbook to the browser.
 *
 * The filename carries the report and the range so a cashier who exports the
 * same report twice in a shift does not overwrite the first one.
 */
export function downloadReportExcel(baseName: string, sheet: XlsxSheet): void {
  const bytes = buildXlsx({ sheets: [sheet], moneyDecimals: safePrecision(moneyPrecision()) });
  // Copy into a fresh buffer: `bytes` is a view, and Blob on some engines
  // takes the whole backing ArrayBuffer when handed one.
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFileName(baseName)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick — Safari has not started the download yet when
  // `click()` returns.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function safeFileName(s: string): string {
  return s
    .replace(/[→·]/g, '-')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/-+/g, '-')
    .slice(0, 120) || 'report';
}
